import { Typography } from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";
import {
  PRICING_CATEGORY_API_KEY_MAP,
  PRICING_CATEGORY_KEYS,
  PRICING_SPECIAL_SETTING_KEYS,
  PricingCategory,
  PricingCategorySelection,
  PricingGeneralSetting,
  PricingSpecialSetting,
  PricingSpecialsPricingRuleType,
} from "../constants";

export interface PricingGeneralSettings {
  [PricingGeneralSetting.InvoiceFee]: boolean;
  [PricingGeneralSetting.CreditInvoiceFee]: boolean;
}

interface PricingSpecialSettings {
  [PricingSpecialSetting.ExpressOrder]?: object;
  [PricingSpecialSetting.OfficeOrder]?: object;
  [PricingSpecialSetting.EarlyBirdOrder]?: object;
  [PricingSpecialSetting.LongOrder]?: object;
  [PricingSpecialSetting.CustomerRecruit]?: object;
}

// TODO proper typing
interface BuildUpdatePricingObjectForApiProps {
  contractId: any;
  contractVersion: any;
  validFrom: any;
  pricingGeneralSettings: any;
  pricingTesList: any;
  backDate?: any;
}

enum StringOption {
  Yes = "yes",
  No = "no",
}

// TODO InvoicingSupplements interface is not up to date!
// interface InvoicingSupplements {
//   invoicingFee: {
//     enabled: boolean;
//   };
//   invoicingFixingFee: {
//     enabled: boolean;
//   };
//   adhocCost: {
//     enabled: boolean;
//     hours: string;
//     pricingRule?: any;
//   };
//   manualOfficeOrderFee: {
//     enabled: boolean;
//     pricingRule?: any;
//   };
// }

// TODO PricingsObject interface is not up to date!
// interface PricingsObject {
//   [key: string]: {
//     [key: string]: {
//       categories: any;
//       // TODO categories property key can be PRICING_CATEGORIES
//       invoicingSupplements: InvoicingSupplements;
//       worktitles: any;
//     };
//   };
// }

export const getPricingSpecials = (apiPricingData?: any) => {
  const specials = {};
  PRICING_SPECIAL_SETTING_KEYS.forEach((specialsKey: string) => {
    const settingsForKey = !!apiPricingData
      ? apiPricingData[specialsKey]
      : undefined;

    const specialsObject = {
      [specialsKey]: {
        enabled: booleanToStringOption(!!settingsForKey),
        settings: settingsForKey || null,
      },
    };

    Object.assign(specials, specialsObject);
  });
  return specials;
};

export const handlePricingData = (pricingsObj: any) => {
  let pricingsArray: any[] = [];
  Object.keys(pricingsObj).forEach((dateKey: string) => {
    const pricingObject = pricingsObj[dateKey];
    const pricingObjectTes = pricingObject.tes;
    pricingsArray.push({
      pricingValidFromDate: dateKey,
      pricingGeneralSettings: {
        invoiceFee: pricingObject.invoiceFee,
        creditInvoiceFee: pricingObject.creditInvoiceFee,
      },
      pricingTesList: Object.keys(pricingObjectTes).map((tesKey: any) => {
        const pricingContentForTes = pricingObjectTes[tesKey];
        const pricingContentForTesWorkTitles =
          pricingContentForTes["worktitles"];

        const getPricingCategoriesForkWorkTitle = (workTitleId: string) => {
          // Create object key and get value from API data, if no data -> undefined
          const getPricingCategoryForWorkTitle = (
            workTitleId: string,
            key: string,
          ) => {
            return {
              [key]: pricingContentForTesWorkTitles[workTitleId][key],
            };
          };
          // Categories
          const categories = {};
          PRICING_CATEGORY_KEYS.forEach((categoryKey: string) => {
            Object.assign(
              categories,
              getPricingCategoryForWorkTitle(workTitleId, categoryKey),
            );
          });
          return categories;
        };

        // Create object key and get value from API data, if no data -> undefined
        const getContentObject = (key: string) => {
          return {
            [key]: pricingContentForTes[key],
          };
        };

        const pricingTesListItem = {
          tesId: tesKey,
          ...getContentObject("categories"),
          specials: getPricingSpecials(pricingContentForTes),
          worktitles: pricingContentForTesWorkTitles
            ? Object.keys(pricingContentForTesWorkTitles).map(
                (workTitleId: any) => {
                  return {
                    workTitleId,
                    categories: getPricingCategoriesForkWorkTitle(workTitleId),
                  };
                },
              )
            : [],
        };

        return pricingTesListItem;
      }),
    });
  });
  // order from oldest to newest
  const sortedPricingsArray = pricingsArray.sort(
    (a: any, b: any) =>
      new Date(b.pricingValidFromDate).getTime() -
      new Date(a.pricingValidFromDate).getTime(),
  );
  return sortedPricingsArray;
};

export const preparePricingCategoriesForApi = (categories: any) => {
  const filteredCategories = {};
  // Filter categories which have NotBilled, "NONE" type selection
  Object.keys(categories).forEach((category: any) => {
    const categoryObj = categories[category];
    if (categoryObj.multiplier.type === PricingCategorySelection.NotBilled)
      return;
    Object.assign(filteredCategories, { [category]: categoryObj });
  });
  return filteredCategories;
};

export const stringOptionToBoolean = (stringOption: StringOption): boolean => {
  return stringOption === StringOption.Yes ? true : false;
};

export const preparePricingSpecialsForApi = (pricingSpecials: any) => {
  const pricingSpecialSettingKeys = PRICING_SPECIAL_SETTING_KEYS;
  const specialsObject = {};
  // If special settings is enabled
  // Set settings content to special key
  pricingSpecialSettingKeys.forEach((key) => {
    const specialsItem = pricingSpecials[key];
    if (specialsItem && stringOptionToBoolean(specialsItem.enabled)) {
      Object.assign(specialsObject, {
        [key]: specialsItem.settings,
      });
    }
  });
  return specialsObject;
};

export const prepareWorkTitlePricingsForApi = (workTitles: any) => {
  const workTitlesObject = {};
  workTitles.forEach((workTitle: any) => {
    Object.assign(workTitlesObject, {
      [workTitle.workTitleId]: {
        ...workTitle.categories,
      },
    });
  });
  return workTitlesObject;
};

export const handleTesObjectForApi = (item: any) => {
  return {
    [item.tesId]: {
      categories: preparePricingCategoriesForApi(item.categories),
      ...preparePricingSpecialsForApi(item.specials),
      ...(item.worktitles && {
        worktitles: prepareWorkTitlePricingsForApi(item.worktitles),
      }),
    },
  };
};

export const preparePricingTesObjectForApi = (pricingTesList: any[]) => {
  const formattedPricingObject = {};
  pricingTesList.forEach((item: any) => {
    const pricingTesObject = handleTesObjectForApi(item);
    Object.assign(formattedPricingObject, pricingTesObject);
  });
  return formattedPricingObject;
};

export const buildUpdatedPricingObjectForApi = ({
  contractId,
  contractVersion,
  validFrom,
  pricingGeneralSettings,
  pricingTesList,
  backDate,
}: BuildUpdatePricingObjectForApiProps) => {
  const formattedPricingTesObject =
    preparePricingTesObjectForApi(pricingTesList);

  const updatedPricingObject = {
    id: contractId,
    version: contractVersion,
    validFrom: validFrom,
    backDate: backDate,
    pricing: {
      invoiceFee: pricingGeneralSettings.invoiceFee,
      creditInvoiceFee: pricingGeneralSettings.creditInvoiceFee,
      tes: formattedPricingTesObject,
    },
  };

  return updatedPricingObject;
};

export const getPricingCategoryObject = (
  category: any,
  dataExists: boolean,
) => {
  // If there is existing data, non existing categories should default to "Not billed" selection
  // Otherwise we have clean form and categories should default to "Multiplier" selection
  const fallbackType = dataExists
    ? PricingCategorySelection.NotBilled
    : PricingCategorySelection.Multiplier;
  return {
    multiplier: {
      type: category?.multiplier?.type || fallbackType,
      value: category?.multiplier?.value || "",
    },
    ...(category?.expenses && { expenses: category.expenses }),
  };
};

export const getPricingCategoriesDefaultValues = (categories?: any) => {
  // Determine if categories data exists or we have clean form
  const dataExists = !!categories;

  return {
    // TYÖ JA POISSAOLOT
    baseHours: getPricingCategoryObject(categories?.baseHours, dataExists),
    supplements: getPricingCategoryObject(categories?.supplements, dataExists),
    taxFreeCompensations: getPricingCategoryObject(
      categories?.taxFreeCompensations,
      dataExists,
    ),
    sickLeave: getPricingCategoryObject(categories?.sickLeave, dataExists),
    midweekHolidays: getPricingCategoryObject(
      categories?.midweekHolidays,
      dataExists,
    ),
    shortTimeWorkingScheme: getPricingCategoryObject(
      categories?.shortTimeWorkingScheme,
      dataExists,
    ),
    holidaySalary: getPricingCategoryObject(
      categories?.holidaySalary,
      dataExists,
    ),
    holidayBonus: getPricingCategoryObject(
      categories?.holidayBonus,
      dataExists,
    ),
  };
};

export const booleanToStringOption = (boolean?: boolean): StringOption => {
  return boolean ? StringOption.Yes : StringOption.No;
};

export const getPricingGeneralSettingsDefaultValues = (
  pricingGeneralSettings?: PricingGeneralSettings,
) => {
  const getDefaultValue = (settingBoolean?: boolean) => {
    if (settingBoolean === undefined) return "";
    return booleanToStringOption(settingBoolean);
  };

  return {
    invoiceFee: getDefaultValue(pricingGeneralSettings?.invoiceFee),
    creditInvoiceFee: getDefaultValue(pricingGeneralSettings?.creditInvoiceFee),
  };
};

export const preparePricingGeneralSettingsForApi = (settings: any) => {
  return {
    invoiceFee: stringOptionToBoolean(settings.invoiceFee),
    creditInvoiceFee: stringOptionToBoolean(settings.creditInvoiceFee),
  };
};

export const getPricingSpecialDefaultValues = (
  specials?: PricingSpecialSettings,
) => {
  // If specials exist, return them
  if (!!specials) return specials;

  return getPricingSpecials();
};

export const getPricingPerTesFormDefaultValues = (pricingData: any) => {
  const { tesId, categories, specials, worktitles } = pricingData;
  return {
    tesId: tesId || null,
    categories: getPricingCategoriesDefaultValues(categories),
    specials: getPricingSpecialDefaultValues(specials),
    worktitles: worktitles || [],
  };
};

export const getWorkTitleSpecificPricingDefaultValues = (
  workTitlePricingData: any,
) => {
  const { workTitleId, categories } = workTitlePricingData;
  return {
    workTitleId: workTitleId || null,
    categories: getPricingCategoriesDefaultValues(categories),
  };
};

export const validatePricingGeneralSettings = (
  pricingGeneralSettings?: PricingGeneralSettings,
) => {
  const isValidBoolean = (setting?: boolean) =>
    setting !== undefined && (setting === true || setting === false);
  const areValid =
    isValidBoolean(pricingGeneralSettings?.invoiceFee) &&
    isValidBoolean(pricingGeneralSettings?.creditInvoiceFee);
  return areValid;
};

export const convertExpensesDataObjectsToArray = (expensesDataObjects: any) => {
  let items: any = [];
  const lookupObj: any = {};
  let index: number = 0;

  Object.keys(expensesDataObjects).forEach((expenseId) => {
    const obj = expensesDataObjects[expenseId];

    // Check if same value and type already exist
    const existingValuesIndex =
      lookupObj?.[obj.multiplier?.value]?.[obj.multiplier?.type]?.index;
    if (typeof existingValuesIndex === "number") {
      items[existingValuesIndex].ids = [
        ...items[existingValuesIndex].ids,
        expenseId,
      ];
      return;
    }

    // Add new item to array
    const newItemObj = {
      itemId: nanoid(), // unique id for error handling
      ids: [expenseId],
      multiplier: obj.multiplier.value,
      type: obj.multiplier.type,
    };
    items = [...items, newItemObj];

    // Add value and type set to lookupObj
    Object.assign(lookupObj, {
      [obj.multiplier.value]: {
        ...lookupObj[obj.multiplier.value],
        [obj.multiplier.type]: {
          index: index,
        },
      },
    });

    // Track items array index position
    index++;
  });

  return items;
};

export const convertExpensesDataArrayToObjects = (array: any) => {
  const expensesObject: any = {};
  array.forEach((object: any) => {
    object.ids.forEach((id: any) => {
      const newObj = {
        [id]: {
          multiplier: {
            value: object.multiplier,
            type: object.type,
          },
        },
      };
      Object.assign(expensesObject, newObj);
    });
  });
  return expensesObject;
};

export const getAllSelectedIds = (array: any) => {
  let selectedIds: string[] = [];
  array.forEach((item: any) => {
    selectedIds = [...selectedIds, ...item.ids];
  });
  return selectedIds;
};

export const buildTesPricingTableDataArray = ({
  pricingTesList,
  tesLocalizationIndex,
}: {
  pricingTesList: any;
  tesLocalizationIndex: any;
}) => {
  if (!pricingTesList) return [];
  return pricingTesList.map((tesItem: any) => ({
    id: tesItem.tesId,
    values: {
      name: (
        <Typography component="span">
          {tesLocalizationIndex[tesItem.tesId]}
        </Typography>
      ),
    },
    deletable: false,
  }));
};

export const buildSpecialPricingTableDataArray = ({
  pricingGeneralSettings,
  label,
}: {
  pricingGeneralSettings?: PricingGeneralSettings;
  label: string | null;
}) => {
  if (!pricingGeneralSettings) return [];
  return [
    {
      id: "general",
      values: {
        name: <Typography component="span">{label}</Typography>,
      },
      deletable: false,
    },
  ];
};

export const getPricingObjectForSpecialRulesAndPricing = ({
  byShiftSelected,
  formData,
}: {
  byShiftSelected?: boolean;
  formData: any;
}) => {
  if (byShiftSelected) {
    return {
      type: PricingSpecialsPricingRuleType.BY_SHIFT,
      shift: formData.costPerShift,
    };
  } else {
    return {
      type: PricingSpecialsPricingRuleType.BY_CATEGORY,
      baseHours: {
        multiplier: {
          type: PricingCategorySelection.Multiplier,
          value: formData.baseHours.multiplier,
        },
        expenses: formData.baseHours.expenses,
      },
      supplements: {
        multiplier: {
          type: PricingCategorySelection.Multiplier,
          value: formData.supplements.multiplier,
        },
        expenses: formData.supplements.expenses,
      },
    };
  }
};

export const getPricingObjectForCustomerRecruit = ({
  formData,
}: {
  formData: any;
}) => {
  const categories = {};
  PRICING_CATEGORY_KEYS.forEach((categoryKey: string) => {
    const typeNotBilled =
      formData[categoryKey].multiplier.type ===
      PricingCategorySelection.NotBilled;
    if (!typeNotBilled && !formData[categoryKey].multiplier.value) return;
    // Other categories need to have a given value, but notBilled is 0
    const multiplier = !typeNotBilled
      ? formData[categoryKey].multiplier
      : {
          type: PricingCategorySelection.NotBilled,
          value: "0",
        };
    const category = {
      [categoryKey]: {
        multiplier,
        expenses: formData[categoryKey].expenses,
      },
    };
    Object.assign(categories, category);
  });

  return {
    type: PricingSpecialsPricingRuleType.BY_CATEGORY,
    ...categories,
  };
};

export const getExpenseTypeCategoryKey = (key: PricingCategory) => {
  return PRICING_CATEGORY_API_KEY_MAP[key];
};
